import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import type { AfterViewInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { EmojiSearch } from '@ctrl/ngx-emoji-mart';
import { categories } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { TranslatePipe } from '@ngx-translate/core';
import { chunk, isString } from 'lodash';
import { UtilityHelper } from '../../../helpers';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxEmojiComponent } from '../../emoji/onyx-emoji/onyx-emoji.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxSearchComponent } from '../../search/onyx-search/onyx-search.component';
import type { OnyxTableViewIcon } from '../../table/interfaces';
import type { OnyxTab } from '../../tabs/interfaces';
import { OnyxTabsComponent } from '../../tabs/onyx-tabs/onyx-tabs.component';
import { ONYX_ICON_PICKER_ICONS } from '../constants';

enum OnyxIconPickerCategory {
  ICONS,
  EMOJIS,
}

@Component({
  selector: 'onyx-icon-picker',
  imports: [
    OnyxEmojiComponent,
    TranslatePipe,
    ScrollingModule,
    OnyxSearchComponent,
    OnyxTabsComponent,
    OnyxIconComponent,
  ],
  templateUrl: './onyx-icon-picker.component.html',
  styleUrl: './onyx-icon-picker.component.scss',
})
export class OnyxIconPickerComponent implements AfterViewInit {
  private readonly injector = inject(Injector);
  private readonly emojiSearch = inject(EmojiSearch);

  public readonly value = model.required<OnyxTableViewIcon>();

  protected readonly I18N = `${I18N_NAMESPACE}.iconPicker`;

  protected readonly TABS: OnyxTab<OnyxIconPickerCategory>[] = [
    { name: `${this.I18N}.icons`, value: OnyxIconPickerCategory.ICONS },
    { name: `${this.I18N}.emojis`, value: OnyxIconPickerCategory.EMOJIS },
  ];
  protected readonly ICONS = ONYX_ICON_PICKER_ICONS;
  protected readonly EMOJI_CATEGORIES = categories;

  protected readonly OnyxIconPickerCategory = OnyxIconPickerCategory;
  protected readonly isString = isString;

  private readonly scrollViewportRef = viewChild(CdkVirtualScrollViewport);

  protected category = signal(OnyxIconPickerCategory.ICONS);
  protected query = signal('');

  protected emojiGroups = computed(() => {
    if (this.query()) {
      const result = this.emojiSearch.search(this.query());
      if (!result?.length) return [];

      return chunk(
        result.map((emoji) => emoji.unified),
        8,
      ).map((group, index) => ({
        id: `search-row-${index}`,
        group,
      }));
    }

    return this.EMOJI_CATEGORIES.flatMap((item) => [
      {
        id: item.id,
        label: `${this.I18N}.${item.id}`,
      },
      ...chunk(item.emojis, 8).map((group, index) => ({
        id: `row-${item.id}-${index}`,
        group,
      })),
    ]);
  });

  public ngAfterViewInit(): void {
    const value = this.value();
    if (!value) return;

    this.category.set(
      UtilityHelper.isEmoji(value)
        ? OnyxIconPickerCategory.EMOJIS
        : OnyxIconPickerCategory.ICONS,
    );

    effect(
      () => {
        if (this.category() !== OnyxIconPickerCategory.EMOJIS) return;

        setTimeout(() => this.scrollToEmoji(value));
      },
      { injector: this.injector },
    );
  }

  protected itemTrackBy(
    _: number,
    item: ReturnType<typeof this.emojiGroups>[number],
  ): string {
    return item.id;
  }

  private scrollToEmoji(emoji: string): void {
    const items = this.emojiGroups();
    const index = items.findIndex(
      (item) => 'group' in item && item.group.includes(emoji),
    );
    if (index === -1) return;

    this.scrollViewportRef()?.scrollToIndex(index, 'instant');
  }
}
